import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ExperimentCard = ({ experiment, onInteract, onViewCode, onVote }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const cardRef = useRef(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (cardRef?.current && isHovered) {
        const rect = cardRef?.current?.getBoundingClientRect();
        setMousePosition({
          x: e?.clientX - rect?.left,
          y: e?.clientY - rect?.top
        });
      }
    };

    if (isHovered) {
      document.addEventListener('mousemove', handleMouseMove);
    }

    return () => document.removeEventListener('mousemove', handleMouseMove);
  }, [isHovered]);

  const handleInteract = () => {
    setIsActive(!isActive);
    onInteract(experiment?.id);
  };

  return (
    <div
      ref={cardRef}
      className={`relative group glass-panel rounded-xl overflow-hidden transition-all duration-500 transform ${
        isHovered ? 'scale-105 cosmic-glow' : ''
      } ${isActive ? 'border-accent' : 'border-border/20'}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Glitch Effect Overlay */}
      <div className={`absolute inset-0 bg-gradient-to-br from-accent/10 to-cosmic-energy/10 opacity-0 transition-opacity duration-300 ${
        isHovered ? 'opacity-100' : ''
      }`} />
      {/* Interactive Cursor Effect */}
      {isHovered && (
        <div
          className="absolute w-32 h-32 bg-accent/20 rounded-full blur-xl pointer-events-none transition-all duration-200"
          style={{
            left: mousePosition?.x - 64,
            top: mousePosition?.y - 64,
            transform: 'translate3d(0, 0, 0)'
          }}
        />
      )}
      {/* Status Badge */}
      <div className="absolute top-4 right-4 z-10">
        <div className={`px-3 py-1 rounded-full text-xs font-accent font-medium ${
          experiment?.status === 'active' ?'bg-success/20 text-success border border-success/30' 
            : experiment?.status === 'beta' ?'bg-warning/20 text-warning border border-warning/30' :'bg-muted/20 text-muted-foreground border border-muted/30'
        }`}>
          {experiment?.status?.toUpperCase()}
        </div>
      </div>
      {/* Preview Image */}
      <div className="relative h-48 overflow-hidden">
        <Image
          src={experiment?.preview}
          alt={experiment?.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
        
        {/* Interaction Button */}
        <button
          onClick={handleInteract}
          className={`absolute bottom-4 left-4 w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 ${
            isActive 
              ? 'bg-accent text-background cosmic-glow' :'bg-surface/80 text-foreground hover:bg-accent hover:text-background'
          }`}
        >
          <Icon name={isActive ? "Pause" : "Play"} size={20} />
        </button>
      </div>
      {/* Content */}
      <div className="p-6">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="font-headline font-bold text-lg text-foreground mb-1">
              {experiment?.title}
            </h3>
            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
              <Icon name="Calendar" size={14} />
              <span>{experiment?.lastUpdated}</span>
              <span>•</span>
              <Icon name="Eye" size={14} />
              <span>{experiment?.views}</span>
            </div>
          </div>
        </div>

        <p className="text-muted-foreground text-sm mb-4 line-clamp-3">
          {experiment?.description}
        </p>

        {/* Technologies */}
        <div className="flex flex-wrap gap-2 mb-4">
          {experiment?.technologies?.map((tech, index) => (
            <span
              key={index}
              className="px-2 py-1 bg-surface/50 text-accent text-xs rounded-md font-accent"
            >
              {tech}
            </span>
          ))}
        </div>

        {/* Interactive Parameters */}
        {experiment?.parameters && experiment?.parameters?.length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-medium text-foreground mb-2">Parameters</h4>
            <div className="space-y-2">
              {experiment?.parameters?.slice(0, 2)?.map((param, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">{param?.name}</span>
                  <div className="flex items-center space-x-2">
                    <input
                      type="range"
                      min={param?.min}
                      max={param?.max}
                      defaultValue={param?.value}
                      className="w-16 h-1 bg-surface rounded-lg appearance-none slider"
                      onChange={(e) => onInteract(experiment?.id, param?.name, e?.target?.value)}
                    />
                    <span className="text-xs text-accent font-accent w-8">
                      {param?.value}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onViewCode(experiment?.id)}
              iconName="Code"
              iconPosition="left"
              className="text-muted-foreground hover:text-accent"
            >
              Code
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onVote(experiment?.id)}
              iconName="Heart"
              iconPosition="left"
              className="text-muted-foreground hover:text-cosmic-energy"
            >
              {experiment?.votes}
            </Button>
          </div>

          <div className="flex items-center space-x-1">
            {experiment?.difficulty && (
              <div className="flex items-center">
                {[...Array(5)]?.map((_, i) => (
                  <Icon
                    key={i}
                    name="Star"
                    size={12}
                    className={i < experiment?.difficulty ? 'text-warning' : 'text-muted/30'}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Glitch Animation */}
      {isActive && (
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 bg-accent/5 animate-pulse" />
          <div className="absolute top-0 left-0 w-full h-px bg-accent animate-pulse" />
          <div className="absolute bottom-0 right-0 w-full h-px bg-cosmic-energy animate-pulse" />
        </div>
      )}
    </div>
  );
};

export default ExperimentCard;